import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { StatusBadge } from '@/shared/components/ui/StatusBadge/StatusBadge';
import { cn } from '@/shared/utils/cn/cn.util';

import { StaffBadge } from '../StaffBadge/StaffBadge';

import { type AccountRefCellProps } from './AccountRefCell.types';
import { useAccountRefCellLogic } from './useAccountRefCellLogic';

/**
 * An account, named rather than numbered — the one cell every queue draws
 * (P27c1: violations, moderation, trust and verification each carried a copy).
 *
 * Three facts, in the order a reviewer reads them: who it is, which kind of
 * account it is, and whether it is already out of service. A `null` account is
 * not an error — the only way to get one is a batched lookup that came back
 * short (P14b), and the row is still one somebody must decide.
 *
 * The status words are the kit's (`uiKit:account.status.*`) because the account
 * lifecycle is one vocabulary the platform over, not one per module.
 */
export function AccountRefCell({
  account,
  statusEmphasis = 'outOfService',
  href = null,
  caption,
  isStatusShown = true,
  className,
}: AccountRefCellProps) {
  const { t } = useTranslation('uiKit');
  const view = useAccountRefCellLogic(account, statusEmphasis);

  if (view.isMissing || view.status === null) {
    return (
      <div className={cn('flex flex-col gap-2xs', className)} data-account="missing">
        <span className="block text-body text-muted-foreground">{t('account.missing')}</span>
        <span className="block text-caption text-muted-foreground">{t('account.missingHint')}</span>
      </div>
    );
  }

  // An LTR ISLAND. An email interpolated into Arabic reorders — the `@` jumps
  // to the far end — and the DOM text stays correct while the rendering is
  // wrong, so nothing but the isolation fixes it.
  const email =
    href === null ? (
      <span dir="ltr" className="block truncate text-body">
        {view.email}
      </span>
    ) : (
      <Link
        dir="ltr"
        to={href}
        className="block truncate text-body underline-offset-4 hover:underline"
      >
        {view.email}
      </Link>
    );

  return (
    <div
      className={cn('flex flex-col items-start gap-2xs', className)}
      data-account={view.isStaff ? 'staff' : 'member'}
    >
      {email}
      <span className="flex flex-wrap items-center gap-xs">
        {isStatusShown ? (
          <StatusBadge tone={view.tone}>{t(`account.status.${view.status}`)}</StatusBadge>
        ) : null}
        <StaffBadge isStaff={view.isStaff} />
      </span>
      {caption !== undefined && caption !== null ? (
        <span className="text-caption text-muted-foreground">{caption}</span>
      ) : null}
    </div>
  );
}
